import { Typography } from '@mui/material'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import tw from 'twin.macro'
import DriverIcon from 'src/assets/icons/driver-front.svg'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { ClientSecureWrapper } from 'src/components/layout/client/ClientSecureWrapper'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { PersonaInquiryType } from 'src/constants/persona'
import { CollectDataDriverLicenseType } from 'src/constants/collect-data/enums'
import { getDriver } from 'src/selectors/driver'
import { Button } from 'src/components/shared/Button'
import { usePersona } from 'src/hooks/usePersona'
import { StepLabels } from 'src/components/client/step-labels/StepLabels'
import { BasicRadioGroup } from 'src/components/shared/form/BasicRadioGroup'
import { TemporaryDriverLicenseForm } from 'src/components/client/driver-license/TemporaryDriverLicenseForm'

const LICENSE_TYPE_OPTIONS = [
  {
    label: 'I have a standard physical driver license',
    value: CollectDataDriverLicenseType.Standard,
  },
  {
    label: 'I have a temporary driver license',
    value: CollectDataDriverLicenseType.Temporary,
  },
]

const DriverLicensePageComponent = () => {
  const driver = useSelector(getDriver)
  const currentStep = useSelector(getCurrentCollectDataStep)
  const [licenseType, setLicenseType] = useState<CollectDataDriverLicenseType | null>(null)

  const { isPersonaLoading, isClientReady, hasInitError, initPersona, openPersona } = usePersona({
    inquiryType: PersonaInquiryType.DriverLicense,
  })

  useEffect(() => {
    if (!currentStep?.id || licenseType !== CollectDataDriverLicenseType.Standard) {
      return
    }

    initPersona(currentStep.id)
  }, [currentStep?.id, licenseType, initPersona])

  const handleStart = useCallback(() => {
    if (hasInitError && currentStep?.id) {
      return initPersona(currentStep.id)
    }

    return openPersona()
  }, [hasInitError, currentStep?.id, initPersona, openPersona])

  const handleLicenseTypeChange = useCallback(
    (_event: React.ChangeEvent<any>, value: string | number) => {
      setLicenseType(value as CollectDataDriverLicenseType)
    },
    [],
  )

  if (!driver) {
    return <div />
  }

  const isStandard = licenseType === CollectDataDriverLicenseType.Standard
  const isTemporary = licenseType === CollectDataDriverLicenseType.Temporary

  return (
    <ClientSecureWrapper
      footer={
        isStandard ? (
          <Button
            className={tw`w-full`}
            loading={isPersonaLoading || (!isClientReady && !hasInitError)}
            onClick={handleStart}
            data-testid="contractors-onboarding-driver-license-start-btn"
          >
            {hasInitError ? 'Retry verification' : 'Start verification process'}
          </Button>
        ) : undefined
      }
    >
      <div
        css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
      >
        <img src={DriverIcon} />
      </div>
      <StepLabels />
      <Typography css={tw`mb-2 mt-2`} variant="h2">
        Verify your Driver's License
      </Typography>
      {!isTemporary && (
        <Typography css={tw`text-shades-400`}>
          To verify your driver's license, we'll use our industry-leading partner, Persona. Tap the
          button below to start the verification.
        </Typography>
      )}
      <Typography css={tw`text-shades-400 mt-6`} variant="overline">
        SELECT PREFERABLE OPTION
      </Typography>
      <div css={[tw`bg-shades-0 p-4 rounded-2xl mt-2`, !isTemporary && tw`mb-4`]}>
        <BasicRadioGroup
          value={licenseType ?? ''}
          options={LICENSE_TYPE_OPTIONS}
          onChange={handleLicenseTypeChange}
          dataTestId="contractors-onboarding-driver-license-type-radio-group"
        />
      </div>
      {isStandard && (
        <InfoContainer dataTestId="contractors-onboarding-driver-license-notice-info" useInfoIcon>
          We'll use Persona to verify your standard license. Tap the button below to begin.
        </InfoContainer>
      )}
      {isTemporary && currentStep?.id && <TemporaryDriverLicenseForm stepId={currentStep.id} />}
    </ClientSecureWrapper>
  )
}

export const DriverLicensePage = memo(DriverLicensePageComponent)
